/* eslint-disable indent, no-param-reassign,no-plusplus, no-unused-vars */
import axios from 'axios';
import Vue from 'vue';
import sortBy from 'lodash/sortBy';
import uniqWith from 'lodash/uniqWith';
import uniq from 'lodash/uniq';
import differenceWith from 'lodash/differenceWith';
import some from 'lodash/some';
import groupBy from 'lodash/groupBy';
import stats from 'stats-lite';
import abbreviateNumber from '../../util/abbreviateNumber';
import cacheAxios from '../../util/cacheAxios';

const benchmarkAxios = axios.create();
const cache = new Map();
cacheAxios(benchmarkAxios, cache);

benchmarkAxios.interceptors.response.use(response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      setTimeout(() => {
        window.location.href = '/sign/in?source=/aims';
      }, 500);
    }
  });

function companyComparator(c1, c2) {
  if (c1.exchange === c2.exchange && c1.stockSymbol === c2.stockSymbol &&
    c1.longName === c2.longName) {
    return true;
  }
  return false;
}
let indicatorsMapping = {};

const lineStyle = {
  normal: {
    width: 3,
    type: 'dotted',
  },
  emphasis: {
    width: 2,
    type: 'dashed',
  },
};
const grid = [
  {
    left: '5%',
    right: '12%',
    bottom: '11%',
    },
  ];
const legend = {
  top: '5%',
  right: '1%',
  orient: 'vertical',
  align: 'right',
};
const label = {
  normal: {
    show: true,
    rotate: '90',
    distance: 20,
    verticalAlign: 'middle',
    // formatter: '{c}',
    formatter: (c) => {
      if (c.data[1] >= 0) {
        return `${abbreviateNumber(c.data[1])}`;
      }
      return `(${abbreviateNumber(-c.data[1])})`;
    },
  },
  emphasis: {
    show: true,
    formatter: '{a}',
  },
};
const companyPlotMaxCount = 10;
export default {
  state: {
    indicatorsMeta: [],
    indicatorSelected: 'totalRevenueUsd',
    filteredCompanies: [],
    competitorsIndicators: {},
    competitors: [],
    chartOptions: {
      title: {
        left: 'center',
        top: '1%',
      },
      backgroundColor: '#fff',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
        // formatter: '{b0}: {c0}<br />{b1}: {c1}',
      },
      grid,
      legend,
      toolbox: {
        feature: {
          magicType: {
            show: true,
            type: ['line', 'bar'],
            title: {
              line: 'line chart',
              bar: 'bar chart',
            },
          },
          dataView: {
            readOnly: true,
            title: 'Data View',
            lang: ['Data View', 'Close', 'Refresh'],
            optionToContent(opt) {
              const axisData = opt.xAxis[0].data;
              const series = opt.series;
              let table = '<table style="width:100%;text-align:center;user-select:text"><tbody><tr><td>Year</td>';
              for (let i = 0; i < series.length; i++) {
                if (series[i].data.length > 0) {
                  table += `<td>${series[i].name}</td>`;
                }
              }
              table += '</tr>';
              for (let i = 0, l = axisData.length; i < l; i++) {
                const year = axisData[i];
                  table += `<tr><td style='width:10%'>${year}</td>`;
                  for (let j = 0; j < series.length; j++) {
                    if (series[j].data.length > 0) {
                      const cell = series[j].data.find(row => row[0] === year);
                      table += `<td style='width:${(90 / series[j].data.length)}%'>${abbreviateNumber(cell) ? abbreviateNumber(cell[1]) : '-'}</td>`;
                    }
                  }
                  table += '</tr>';
              }
              table += '</tbody></table>';
              return table;
          },
          },
          restore: {
            show: true,
            title: 'reset',
          },
          saveAsImage: {
            show: true,
            title: 'save',
          },
        },
      },
      series: [{
        name: 'company',
        type: 'bar',
        barWidth: '25%',
        data: [],
      }],
      xAxis: {
        type: 'category',
        data: [],
        axisPointer: {
          type: 'shadow',
        },
      },
      yAxis: {
        splitLine: {
          show: true,
        },
        axisLabel: {
          type: 'value',
          formatter: (lbl) => {
            if (lbl >= 0) {
              return `US$${abbreviateNumber(lbl)}`;
            }
            return `(US$${abbreviateNumber(-lbl)})`;
          },
        },
      },
    },
  },
  mutations: {
    setIndicatorsMeta(state, metas) {
      state.indicatorsMeta = metas;
    },
    removeAllCompetitors(state) {
      state.competitors = [];
    },
    removeCompetitors(state, companies) {
      state.competitors = differenceWith(state.competitors, companies, companyComparator);
    },
    appendCompetitors(state, companies) {
      state.competitors = uniqWith(state.competitors.concat(companies), companyComparator);
    },
    addCompetitorsIndicators(state, competitors) {
      const metrics = state.indicatorsMeta.find(e => e.key === 'Productivity Metric');
      const financeMetrics = state.indicatorsMeta.find(e => e.key === 'Financial Indicator');
      if (metrics && metrics.children.length > 0) {
        competitors = competitors.map((ind) => {
          if (ind.totalRevenueUsd && ind.netIncomeUsd) {
            ind.totalCostUsd = ind.totalRevenueUsd - ind.netIncomeUsd;
          }
          metrics.children.forEach((met) => {
            if (ind[met.numerator] && ind[met.denominator]) {
              ind[met.key] = ind[met.numerator] / ind[met.denominator];
            }
          });
          financeMetrics.children.forEach((met) => {
            if (met.numerator && met.denominator && ind[met.numerator] && ind[met.denominator]) {
              ind[met.key] = ind[met.numerator] / ind[met.denominator];
            }
          });
          return ind;
        });
      }
      competitors = groupBy(competitors, c => `${c.longName}|-|${c.exchange}|-|${c.stockSymbol}`);
      state.competitorsIndicators = Object.freeze({
        ...state.competitorsIndicators,
        ...competitors,
      });
    },
    setFilteredCompanies(state, companies) {
      state.filteredCompanies = companies;
    },
    setIndicatorSelected(state, indicator) {
      state.indicatorSelected = indicator;
    },
    plotPinnedRevenueAndCosts(state, company) {
      const title = {
        ...state.chartOptions.title,
      };
      if (company === '') {
        title.text = indicatorsMapping[state.indicatorSelected];
        title.subtext = '';
        const xAxis = {
          ...state.chartOptions.xAxis,
          data: [],
        };
        const series = [{
            name: 'company',
            type: 'bar',
            barWidth: '25%',
            data: [],
          }, {
            name: 'company',
            type: 'bar',
            barWidth: '25%',
            data: [],
          }, {
            name: 'company',
            type: 'bar',
            barWidth: '25%',
            data: [],
          },
        ];
        state.chartOptions = {
          ...state.chartOptions,
          clear: true,
          xAxis,
          title,
          series,
        };
        return;
      }
      title.subtext = indicatorsMapping[state.indicatorSelected];
      title.text = state.competitorsIndicators[company][0].longName;

      const sortedIndicators = sortBy(state.competitorsIndicators[company], ['reportDate']);
      const years = sortedIndicators.map(i => `${i.reportDate}`);
      let trKey = 'totalRevenueUsd';
      let tcKey = 'totalCostUsd';
      let scKey = 'salaryCostUsd';
      if (state.indicatorSelected === 'revenueAndCostPerEmployee') {
        trKey = 'totalRevenueUsdPerEmployee';
        tcKey = 'totalCostUsdPerEmployee';
        scKey = 'salaryCostUsdPerEmployee';
      }
      const tr = sortedIndicators.map((i) => {
        const value = i[trKey] ? i[trKey].toFixed(2) : '';
        const v = [
          `${i.reportDate}`,
          value,
        ];
        return v;
      });
      const tc = sortedIndicators.map((i) => {
        const value = i[tcKey] ? i[tcKey].toFixed(2) : '';
        const v = [
          `${i.reportDate}`,
          value,
        ];
        return v;
      });
      const sc = sortedIndicators.map((i) => {
        const value = i[scKey] ? i[scKey].toFixed(2) : '';
        const v = [
          `${i.reportDate}`,
          value,
        ];
        return v;
      });
      const xAxis = {
        ...state.chartOptions.xAxis,
        data: years,
      };
      const series = [
        {
          name: 'company',
          type: 'bar',
          barWidth: '25%',
          data: [],
        }, {
          name: 'company',
          type: 'bar',
          barWidth: '25%',
          data: [],
        }, {
          name: 'company',
          type: 'bar',
          barWidth: '25%',
          data: [],
        },
      ];
      series[0].name = `${state.competitorsIndicators[company][0].longName} (Total Revenue)`;
      series[0].data = tr;
      series[1].name = `${state.competitorsIndicators[company][0].longName} (Total Cost)`;
      series[1].data = tc;
      series[2].name = `${state.competitorsIndicators[company][0].longName} (Staff Cost)`;
      series[2].data = sc;
      const legendData = series.map(s => s.name);
      const newLegend = {
        ...legend,
        data: legendData,
      };
      state.chartOptions = {
        ...state.chartOptions,
        clear: true,
        title,
        xAxis,
        series,
        legend: newLegend,
      };
    },
    plotPinnedChart(state, company) {
      const title = {
        ...state.chartOptions.title,
      };
      if (company === '') {
        title.text = indicatorsMapping[state.indicatorSelected];
        title.subtext = '';
        const series = [
          ...state.chartOptions.series,
        ];
        series[0].data = [];
        series[0].name = '';

        state.chartOptions = {
          ...state.chartOptions,
          clear: true,
          title,
          series,
        };
        return;
      }
      title.subtext = indicatorsMapping[state.indicatorSelected];
      title.text = state.competitorsIndicators[company][0].longName;
      const yAxis = {
        ...state.chartOptions.yAxis,
      };
      if (state.indicatorSelected === 'noOfEmployees'
        || state.indicatorSelected === 'operatingMarginUsd'
        || state.indicatorSelected === 'rulcUsd'
        || state.indicatorSelected === 'salaryCostUsdToTotalRevenueUsd'
        || state.indicatorSelected === 'salaryCostUsdToNetIncomeUsd'
        || state.indicatorSelected === 'netIncomeUsdToSalaryCostUsd'
        || state.indicatorSelected === 'percentageWomenManagement'
        || state.indicatorSelected === 'percentageWomenWorkforce'
      ) {
        yAxis.axisLabel.formatter = (lbl) => {
          if (lbl >= 0) {
            return `${abbreviateNumber(lbl)}`;
          }
          return `(${abbreviateNumber(-lbl)})`;
        };
      } else {
        yAxis.axisLabel.formatter = (lbl) => {
          if (lbl >= 0) {
            return `US$${abbreviateNumber(lbl)}`;
          }
          return `(US$${abbreviateNumber(-lbl)})`;
        };
      }
      const sortedIndicators = sortBy(state.competitorsIndicators[company], ['reportDate']);
      const years = sortedIndicators.map(i => `${i.reportDate}`);
      const values = sortedIndicators.map((i) => {
        let value = '';
        if (state.indicatorSelected !== 'epsNetIncomeBasicUsd'
          && state.indicatorSelected !== 'operatingMarginUsd'
          && state.indicatorSelected !== 'rulcUsd') {
          value = i[state.indicatorSelected] ? i[state.indicatorSelected].toFixed(0) :
            '';
        } else {
          value = i[state.indicatorSelected] ? i[state.indicatorSelected].toFixed(3) :
            '';
        }
        const v = [
          `${i.reportDate}`,
          value,
        ];
        return v;
      });

      const xAxis = {
        ...state.chartOptions.xAxis,
        data: years,
      };
      const series = [...state.chartOptions.series];
      series[0].name = state.competitorsIndicators[company][0].longName;
      series[0].data = values;
      const legendData = series.map(s => s.name);
      const newLegend = {
        ...legend,
        data: legendData,
      };
      state.chartOptions = {
        ...state.chartOptions,
        clear: true,
        title,
        xAxis,
        yAxis,
        series,
        legend: newLegend,
      };
    },
    plotCompetitorsChartCombine(state, {
      xAxisData,
      mSeries,
    }) {
      const series = mSeries;
      let data = [...state.chartOptions.xAxis.data, ...xAxisData];
      data = uniq(data);
      data.sort();
      const xAxis = {
        ...state.chartOptions.xAxis,
        data,
      };
      const newLegend = {
        ...legend,
        data: series.map((s) => {
          if (s) {
            return s.name;
          }
          return '';
        })
          .filter(n => n && n !== 'company'),
      };
      if (xAxisData.length === 0) {
        newLegend.data = [];
      }
      state.chartOptions = {
        ...state.chartOptions,
        clear: true,
        xAxis,
        title: { ...state.chartOptions.title,
          text: indicatorsMapping[state.indicatorSelected],
        },
        // yAxis,
        series,
        legend: newLegend,
      };
    },
    plotCompetitorsChart(state, {
      xAxisData,
      mSeries,
    }) {
      const series0 = {
        ...state.chartOptions.series[0],
        type: 'bar',
        barWidth: '25%',
      };
      const series = [series0, ...mSeries];
      // let yMin = Number.MAX_SAFE_INTEGER;
      // series.forEach((s) => {
      //   s.data.forEach((d) => {
      //     if (!d) return;
      //     if (+d[1] < yMin) {
      //       yMin = +d[1];
      //     }
      //   });
      // });
      // const yAxis = {
      //   ...state.chartOptions.yAxis,
      //   min: Math.floor(yMin > 0 ? yMin * 0.95 : yMin * 1.05),
      // };
      const yAxis = {
        ...state.chartOptions.yAxis,
      };
      if (state.indicatorSelected === 'noOfEmployees'
        || state.indicatorSelected === 'operatingMarginUsd'
        || state.indicatorSelected === 'rulcUsd'
        || state.indicatorSelected === 'salaryCostUsdToTotalRevenueUsd'
        || state.indicatorSelected === 'salaryCostUsdToNetIncomeUsd'
        || state.indicatorSelected === 'netIncomeUsdToSalaryCostUsd'
        || state.indicatorSelected === 'percentageWomenManagement'
        || state.indicatorSelected === 'percentageWomenWorkforce') {
        yAxis.axisLabel.formatter = (lbl) => {
          if (lbl >= 0) {
            return `${abbreviateNumber(lbl)}`;
          }
          return `(${abbreviateNumber(-lbl)})`;
        };
      } else {
        yAxis.axisLabel.formatter = (lbl) => {
          if (lbl >= 0) {
            return `US$${abbreviateNumber(lbl)}`;
          }
          return `(US$${abbreviateNumber(-lbl)})`;
        };
      }
      let data = [...state.chartOptions.xAxis.data, ...xAxisData];
      data = uniq(data);
      data.sort();

      const xAxis = {
        ...state.chartOptions.xAxis,
        data,
      };
      const newLegend = {
        ...legend,
        data: series.map(s => s.name).filter(n => n && n !== 'company'),
      };
      const title = {
        ...state.chartOptions.title,
        text: indicatorsMapping[state.indicatorSelected],
      };
      state.chartOptions = {
        ...state.chartOptions,
        clear: true,
        xAxis,
        yAxis,
        series,
        title,
        legend: newLegend,
      };
    },
  },
  actions: {

    saveCompanyList(_, list) {
      return benchmarkAxios.post('/aims/api/benchmark/saveCompanyList', list);
    },

    retrieveCompanyList(_, list) {
      return benchmarkAxios.get('/aims/api/benchmark/userCompanyList');
    },

    removeCompanyList(_, listName) {
      return benchmarkAxios.delete(`/aims/api/benchmark/userCompanyList/${listName}`);
    },

    preparePlotCompetitorCombine({
      commit,
      state,
    }, companySelections) {
      let trKey = 'totalRevenueUsd';
      let tcKey = 'totalCostUsd';
      let scKey = 'salaryCostUsd';
      if (state.indicatorSelected === 'revenueAndCostPerEmployee') {
        trKey = 'totalRevenueUsdPerEmployee';
        tcKey = 'totalCostUsdPerEmployee';
        scKey = 'salaryCostUsdPerEmployee';
      }
      const solidLineStyle = {
        normal: {
          width: 3,
          type: 'solid',
        },
      };

      let relevantIndicators = [];
      companySelections.forEach((c) => {
        const matches = state.competitorsIndicators[
          `${c.longName}|-|${c.exchange}|-|${c.stockSymbol}`];
        relevantIndicators = relevantIndicators.concat(matches);
      });

      const grouped = groupBy(relevantIndicators, 'reportDate');
      let dates = Object.keys(grouped);
      const series = [{
        name: 'Total Revenue (median)',
        type: 'line',
        data: [],
        lineStyle: solidLineStyle,
        label,
      }, {
        name: 'Total Cost (median)',
        type: 'line',
        data: [],
        lineStyle: solidLineStyle,
        label,
      }, {
        name: 'Staff Cost (median)',
        type: 'line',
        data: [],
        lineStyle: solidLineStyle,
        label,
      }];

      const tr = {};
      const tc = {};
      const sc = {};
      let trMax = 0;
      let tcMax = 0;
      let scMax = 0;
      dates.forEach((date) => {
        tr[date] = grouped[date]
          .filter(l => l[trKey])
          .map(l => l[trKey]);
        tc[date] = grouped[date]
          .filter(l => l[tcKey])
          .map(l => l[tcKey]);
        sc[date] = grouped[date]
          .filter(l => l[scKey])
          .map(l => l[scKey]);
        if (tr[date].length > trMax) {
          trMax = tr[date].length;
        }
        if (tc[date].length > tcMax) {
          tcMax = tc[date].length;
        }
        if (sc[date].length > scMax) {
          scMax = sc[date].length;
        }
      });
      const validDates = {};
      dates.forEach((date) => {
        if (tr[date].length >= trMax * 0.8) {
          const trMid = stats.median(tr[date]);
          series[0].data.push([date, trMid.toFixed(0)]);
          validDates[date] = true;
        }
        if (tc[date].length >= tcMax * 0.8) {
          const tcMid = stats.median(tc[date]);
          series[1].data.push([date, tcMid.toFixed(0)]);
          validDates[date] = true;
        }
        if (sc[date].length >= scMax * 0.8) {
          const scMid = stats.median(sc[date]);
          series[2].data.push([date, scMid.toFixed(0)]);
          validDates[date] = true;
        }
      });
      dates = dates.filter(date => validDates[date]);
      let mSeries;
      if (state.chartOptions.series.length > 0 && !state.chartOptions.series[0].name.includes('(Total Revenue)')) {
        mSeries = series;
      } else {
        mSeries = [
          ...state.chartOptions.series.slice(0, 3),
          ...series,
        ];
      }
      commit('plotCompetitorsChartCombine', {
        xAxisData: dates,
        mSeries,
      });
    },
    preparePlotCompetitorChart({
      dispatch,
      commit,
      state,
    }, companySelections) {
      if (state.indicatorSelected === 'revenueAndCost' || state.indicatorSelected ===
        'revenueAndCostPerEmployee') {
        dispatch('preparePlotCompetitorCombine', companySelections);
        return;
      }
      let dates = [];
      let series = [];
      if (companySelections.length <= companyPlotMaxCount) {
        let relevantIndicators = [];
        companySelections.forEach((c) => {
          const matches = state.competitorsIndicators[
            `${c.longName}|-|${c.exchange}|-|${c.stockSymbol}`];
          relevantIndicators = relevantIndicators.concat(matches);
        });
        const grouped = groupBy(relevantIndicators, 'longName');
        const sorted = Object.keys(grouped)
          .map(key => sortBy(grouped[key], ['reportDate']));
        sorted.forEach(arr => arr.forEach((item) => {
          if (!dates.includes(`${item.reportDate}`)) {
            dates.push(`${item.reportDate}`);
          }
        }));
        dates.sort();
        series = Object.keys(grouped)
          .map((key, i) => {
            const array = {
              name: key,
              type: 'line',
              data: sorted[i].map((d) => {
                  let value = d[state.indicatorSelected] ? d[state.indicatorSelected] : '';
                  if (value && Math.abs(value) > 100) {
                    value = value.toFixed(0);
                  } else if (value) {
                    value = value.toFixed(2);
                  }
                  const v = [
                  `${d.reportDate}`,
                  value,
                ];
                  return v;
                })
                .filter(d => d[1] !== ''),
              lineStyle,
              label,
            };
            return array;
          })
          .filter(item => item.data.length > 0);
      } else {
        const solidLineStyle = {
          normal: {
            width: 3,
            type: 'solid',
          },
        };
        let relevantIndicators = [];
        companySelections.forEach((c) => {
          const matches = state.competitorsIndicators[
            `${c.longName}|-|${c.exchange}|-|${c.stockSymbol}`];
          relevantIndicators = relevantIndicators.concat(matches);
        });
        const grouped = groupBy(relevantIndicators, 'reportDate');
        dates = Object.keys(grouped);
        for (let i = 0; i < 3; i++) {
          let name = '';
          switch (i) {
          case 0:
            name = '25%';
            break;
          case 1:
            name = 'Median';
            break;
          case 2:
            name = '75%';
            break;
          default:
          }
          series.push({
            name,
            type: 'line',
            data: [],
            lineStyle: solidLineStyle,
          });
        }
        let max = 0;
        const validDates = {};
        dates.forEach((date) => {
          grouped[date] = grouped[date]
            .filter(l => l[state.indicatorSelected])
            .map(l => l[state.indicatorSelected]);
          if (grouped[date].length > max) {
            max = grouped[date].length;
          }
        });
        dates.forEach((date) => {
          const filteredList = grouped[date];
          if (filteredList.length < max * 0.8) {
            return;
          }
          validDates[date] = true;
          let low = stats.percentile(filteredList, 0.25);
          let mid = stats.median(filteredList);
          let hi = stats.percentile(filteredList, 0.75);
          if (hi && Math.abs(hi) > 100) {
            low = low.toFixed(0);
            hi = hi.toFixed(0);
            mid = mid.toFixed(0);
          } else if (hi) {
            low = low.toFixed(2);
            hi = hi.toFixed(2);
            mid = mid.toFixed(2);
          }
          // if (state.indicatorSelected !== 'epsNetIncomeBasicUsd'
          //   && state.indicatorSelected !== 'operatingMarginUsd'
          //   && state.indicatorSelected !== 'rulcUsd') {
          //   low = low.toFixed(0);
          //   hi = hi.toFixed(0);
          //   mid = mid.toFixed(0);
          // } else {
          //   low = low.toFixed(3);
          //   hi = hi.toFixed(3);
          //   mid = mid.toFixed(3);
          // }
          series[0].data.push([date, low]);
          series[1].data.push([date, mid]);
          series[2].data.push([date, hi]);
        });
        dates = dates.filter(date => validDates[date]);
      }
      commit('plotCompetitorsChart', {
        xAxisData: dates,
        mSeries: series,
      });
    },
    retrieveCompetitorsIndicators({
      state,
      commit,
    }, competitors) {
      const diff = competitors.filter(c => !state.competitorsIndicators[
        `${c.longName}|-|${c.exchange}|-|${c.stockSymbol}`]);
      const coms = diff.map(com => ({
        e: com.exchange,
        s: com.stockSymbol,
        l: com.longName,
      }));
      return benchmarkAxios.post('/aims/api/benchmark/companyIndicators', coms)
        .then((res) => {
          if (res.data !== null) {
            commit('addCompetitorsIndicators', res.data);
          }
        });
    },
    getIndicatorsMetas({
      commit,
    }) {
      return benchmarkAxios.get('/aims/api/benchmark/indicatorsMeta')
        .then((res) => {
          if (res.data) {
            commit('setIndicatorsMeta', res.data);
            indicatorsMapping = {};
            res.data.forEach((grp) => {
              grp.children.forEach((meta) => { indicatorsMapping[meta.key] = meta.label; });
            });
          }
        });
    },
    filterAndPlot({
      commit,
      dispatch,
    }, filter) {
      return benchmarkAxios.post('/aims/api/benchmark/companiesFilter', filter, {
          cache: true,
          cacheKey: filter,
        })
        .then((res) => {
          if (res.data) {
            commit('removeAllCompetitors');
            commit('appendCompetitors', res.data);
            commit('setFilteredCompanies', res.data);
          } else {
            commit('appendCompetitors', []);
            commit('setFilteredCompanies', []);
          }
        });
    },
    searchLongName({
      commit,
    }, longName) {
      if (longName.trim() === '') {
        commit('setFilteredCompanies', []);
        return Promise.resolve();
      }
      return benchmarkAxios.get(`/aims/api/benchmark/longNameFilter/${longName}`, {
          cache: true,
          cacheKey: longName,
        })
        .then((res) => {
          if (res.data) {
            commit('setFilteredCompanies', res.data);
          } else {
            commit('setFilteredCompanies', []);
          }
        });
    },
    filterCompanies({
      commit,
    }, filter) {
      const keysWithContent = Object.keys(filter)
        .filter(key => filter[key]);
      if (keysWithContent.length === 0) {
        commit('setFilteredCompanies', []);
        return Promise.resolve();
      }
      return benchmarkAxios.post('/aims/api/benchmark/companiesFilter', filter, {
          cache: true,
          cacheKey: filter,
        })
        .then((res) => {
          if (res.data) {
            commit('setFilteredCompanies', res.data);
          } else {
            commit('setFilteredCompanies', []);
          }
        });
    },
  },
  getters: {},
};



// WEBPACK FOOTER //
// ./src/store/modules/benchmarkModule.js